import { useAuth0 } from "@auth0/auth0-react";
import { ArrowRight, CaretDown, CaretUp } from "@carbon/icons-react";
import {
  Button, InlineNotification, SkeletonPlaceholder, SkeletonText, StructuredListBody,
  StructuredListCell, StructuredListHead, StructuredListRow, StructuredListSkeleton,
  StructuredListWrapper, Tab, TabList, TabPanel, TabPanels, Tabs
} from "@carbon/react";
import axios from "axios";
import Color from "color";
import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import LineChart from "../../components/LineChart";
import SelectReportDate from "../../components/SelectReportDate/SelectReportDate";
import ReportDateContext from "../../ReportDateContext";

import wspColor from "../../wspColours.scss";

const Dashboard = () => {
  const { getAccessTokenSilently } = useAuth0();

  const { year, month, useActual, selectedReport, availableReports, setReportDate, setUseActual } = useContext(ReportDateContext);
  const [loading, setLoading] = useState(true);
  const [groupUseIntensity, setGroupUseIntensity] = useState([]);
  const [overallEUI, setOverallEUI] = useState(0);
  const [overallEUIChange, setOverallEUIChange] = useState(0);
  const [overallWUI, setOverallWUI] = useState(0);
  const [overallWUIChange, setOverallWUIChange] = useState(0);
  const [monthlyEUI, setMonthlyEUI] = useState([]);
  const [monthlyWUI, setMonthlyWUI] = useState([]);
  const [lowestEUI, setLowestEUI] = useState([]);
  const [highestEUI, setHighestEUI] = useState([]);
  const [lowestWUI, setLowestWUI] = useState([]);
  const [highestWUI, setHighestWUI] = useState([]);

  useEffect(() => {
    const formatUiChartData = (uiData, color) =>
      Object.entries(uiData.values).map(([key, value]) => ({
        labels: uiData.labels,
        datasets: [
          {
            label: key,
            data: value,
            backgroundColor: color.alpha(0.5).string(),
            borderColor: color.string(),
            borderWidth: 1,
            fill: "start",
            tension: 0.2
          }
        ]
      }));

    const getData = async () => {
      try {
        setLoading(true);
        const accessToken = await getAccessTokenSilently();
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_SERVER_URL}/data/dashboard/${year}/${month}${useActual ? "?actual" : ""}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`
            }
          }
        );

        // Set page data
        setGroupUseIntensity(data.group_ui);
        setOverallEUI(data.overall_eui);
        setOverallEUIChange(data.overall_eui_change);
        setOverallWUI(data.overall_wui);
        setOverallWUIChange(data.overall_wui_change);
        setMonthlyEUI(formatUiChartData(data.monthly_eui, Color(wspColor.red01)));
        setMonthlyWUI(formatUiChartData(data.monthly_wui, Color(wspColor.charcoaldark)));
        setLowestEUI(data.lowest_5_eui);
        setHighestEUI(data.highest_5_eui);
        setLowestWUI(data.lowest_5_wui);
        setHighestWUI(data.highest_5_wui);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    if (year !== null && month !== null) {
      getData();
    }
  }, [month, year, useActual, getAccessTokenSilently]);

  /*
   * Page rendering functions
   */
  const pctChangeLabel = pctChange => (
    <p className="dashboard__pctChange">
      {pctChange > 0 ? <CaretUp size={32} title="up" /> : <CaretDown size={32} title="down" />}
      {`${Math.abs(Math.round(pctChange * 1000) / 10)}%`}
    </p>
  );

  const createUseIntensityVal = (label, val, change) => (
    <div>
      {loading ? (
        <SkeletonText paragraph lineCount={3} />
      ) : (
        <>
          <h4>{label}</h4>
          <p className="dashboard__overall_ui_value">{val}</p>
          <p className="dashboard__pctChange">
            {change > 0 ? <CaretUp size={32} title="up" /> : <CaretDown size={32} title="down" />}
            {`${Math.abs(Math.round(change * 1000) / 10)}% from previous year`}
          </p>
        </>
      )}
    </div>
  );

  const createChartTabs = (tabChartData, unitLabel) => (
    <div className="cds--col-lg-6 cds--col-md-8 dashboard__use_intensity_chart">
      {loading ? (
        <SkeletonPlaceholder className="dashboard__skeleton_placeholder" />
      ) : (
        <Tabs>
          <TabList aria-label="List of tabs">
            {tabChartData.map(d => <Tab key={`tab-${d.datasets[0].label}`}>{d.datasets[0].label}</Tab>)}
          </TabList>
          <TabPanels>
            {tabChartData.map(d => (
              <TabPanel key={`tabPanel-${d.datasets[0].label}`}>
                <div>
                  <LineChart
                    chartId={`dashboard-linechart-ui-${d.datasets[0].label}`}
                    chartData={d}
                    aspectRatio={1.75}
                    labelYAxis="Use Intensity"
                    unitYAxis={unitLabel}
                  />
                </div>
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      )}
    </div>
  );

  const createRankingTable = (title, rankedList, key) => (
    <div className="cds--col-lg-4 cds--col-md-4 cds--col-sm-4">
      <div>
        <h4>{title}</h4>
        {loading ? (
          <StructuredListSkeleton />
        ) : (
          <StructuredListWrapper>
            <StructuredListHead>
              <StructuredListRow head>
                <StructuredListCell head>Building</StructuredListCell>
                <StructuredListCell head>Value</StructuredListCell>
              </StructuredListRow>
            </StructuredListHead>
            <StructuredListBody>
              {rankedList.map(d => (
                <StructuredListRow key={`${key}-${d.bid}`}>
                  <StructuredListCell>
                    <h5>
                      <Link to="/building_report" state={{ building: d.bid }}>
                        {`${d.bname}${d.partial ? "*" : ""}`}
                        <sup>{`${useActual || d.normalized ? "" : "†"}`}</sup>
                      </Link>
                    </h5>
                    <p>{d.group}</p>
                  </StructuredListCell>
                  <StructuredListCell>
                    <p>{d[key]}</p>
                    {pctChangeLabel(d.rel_change)}
                  </StructuredListCell>
                </StructuredListRow>
              ))}
            </StructuredListBody>
          </StructuredListWrapper>
        )}
      </div>
    </div>
  );

  return (
    <>
      <SelectReportDate
        id="selectedReportDate"
        availableReports={availableReports}
        selectedReport={selectedReport}
        label="Available Reports"
        onChange={setReportDate}
        useActual={useActual}
        onChangeuseActual={setUseActual}
        light
      />

      <section className="cds--grid dashboard__overall_portfolio">
        <div className="cds--row">
          <div className="cds--col">
            <div className="cds--row">
              <div className="cds--col-lg-4 cds--col-md-8 dashboard__use_intensity">
                <h2>All Buildings</h2>
                <div>
                  {createUseIntensityVal("EUI", overallEUI, overallEUIChange)}
                  {createUseIntensityVal("WUI", overallWUI, overallWUIChange)}
                </div>
              </div>
              {createChartTabs(monthlyEUI, "ekWh/sq. ft")}
              {createChartTabs(monthlyWUI, "L/sq. ft")}
            </div>
          </div>
        </div>
      </section>

      <section className="cds--grid dashboard__header_block">
        <div className="cds--row">
          <div className="cds--col dashboard__header_portfolio_list">
            <div>
              <h3>Building Portfolios</h3>
              {loading ? (
                <StructuredListSkeleton />
              ) : (
                <StructuredListWrapper>
                  <StructuredListHead>
                    <StructuredListRow head>
                      <StructuredListCell head>Portfolio</StructuredListCell>
                      <StructuredListCell head>EUI</StructuredListCell>
                      <StructuredListCell head>WUI</StructuredListCell>
                      <StructuredListCell head> </StructuredListCell>
                    </StructuredListRow>
                  </StructuredListHead>
                  <StructuredListBody>
                    {groupUseIntensity.map(d => (
                      <StructuredListRow key={`groupui_${d.groupid}`}>
                        <StructuredListCell>{d.group}</StructuredListCell>
                        <StructuredListCell>
                          <p>{d.eui}</p>
                          {pctChangeLabel(d.eui_change)}
                        </StructuredListCell>
                        <StructuredListCell>
                          <p>{d.wui}</p>
                          {pctChangeLabel(d.wui_change)}
                        </StructuredListCell>
                        <StructuredListCell>
                          <Link to="/building_portfolio" state={{ group: d.groupid }}>
                            <Button renderIcon={ArrowRight} size="sm">
                              View Portfolio
                            </Button>
                          </Link>
                        </StructuredListCell>
                      </StructuredListRow>
                    ))}
                  </StructuredListBody>
                </StructuredListWrapper>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="cds--grid dashboard__ui_ranks">
        <div className="cds--row">
          {createRankingTable("Lowest 5 EUIs", lowestEUI, "eui")}
          {createRankingTable("Highest 5 EUIs", highestEUI, "eui")}
          {createRankingTable("Lowest 5 WUIs", lowestWUI, "wui")}
          {createRankingTable("Highest 5 WUIs", highestWUI, "wui")}
        </div>
        <div className="cds--row">
          <div className="cds--col dashboard__ui_ranks_notes">
            <InlineNotification
              title="Note:"
              kind="info"
              hideCloseButton
              lowContrast
            >
              * - denotes building usage intensity values calculated with a
              partial utility set (i.e. usage data for one or more utilities
              unavailable)
              {!useActual && (
                <>
                  <br />
                  † - denotes buildings where normalized usage data is fully
                  or partially unavailable; reported data contains actual
                  usage
                </>
              )}
            </InlineNotification>
          </div>
        </div>
      </section>
    </>
  );
};

export default Dashboard;
